
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import { Operation, PermissionCode, User } from '@/types';
import { formatPaymentsDetails } from '@/helpers';

@Component
export default class OperationPayments extends Vue {
  @Prop(Object) readonly operation!: Operation;

  loading = false

  paymentsNotes: string | null = null
  getPaymentsNotesTimeout: number | undefined

  @Watch('amIUpdating')
  onAmIUpdatingChange(val: boolean, oldVal: boolean) {
    if (oldVal === true && val === false) {
      // La commessa è stata aggiornata
      // Se ci sono note, recupero la nuova versione
      if (this.hasPaymentsNotes && this.paymentsNotes) {
        this.getPaymentsNotes();
      }
    }
  }

  get poolUpdating(): { [key: number]: boolean } {
    return this.$store.getters.currentOperationsPoolUpdating;
  }

  get amIUpdating(): boolean {
    return this.operation
      && this.operation.id in this.poolUpdating
      && this.poolUpdating[this.operation.id] === true;
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get invoiceDiscount() {
    return this.operation.invoiceDiscount;
  }

  get loan() {
    return this.operation.loan;
  }

  get invoiceDiscountIconTooltipText() {
    if (this.invoiceDiscount === 'yes') return 'Sconto in fattura: sì';
    if (this.invoiceDiscount === 'no') return 'Sconto in fattura: no';
    return 'Sconto in fattura: non definito';
  }

  get loanIconTooltipText() {
    if (this.loan === 'yes') return 'Finanziamento: sì';
    if (this.loan === 'no') return 'Finanziamento: no';
    return 'Finanziamento: non definito';
  }

  get invoiceDiscountIconColor(): string {
    if (this.invoiceDiscount === 'yes') return 'success';
    if (this.invoiceDiscount === 'no') return 'grey lighten-2';
    return 'grey lighten-2';
  }

  get loanIconColor(): string {
    if (this.loan === 'yes') return 'success';
    if (this.loan === 'no') return 'grey lighten-2';
    return 'grey lighten-2';
  }

  get invoiceDiscountTooltipColor(): string {
    if (this.invoiceDiscount === 'yes') return 'success';
    if (this.invoiceDiscount === 'no') return '';
    return '';
  }

  get loanTooltipColor(): string {
    if (this.loan === 'yes') return 'success';
    if (this.loan === 'no') return '';
    return '';
  }

  get invoiceDiscountIcon(): string {
    if (this.invoiceDiscount === 'yes') return 'mdi-sale';
    if (this.invoiceDiscount === 'no') return 'mdi-sale';
    return 'mdi-sale-outline';
  }

  get loanIcon(): string {
    if (this.loan === 'yes') return 'mdi-hand-coin';
    if (this.loan === 'no') return 'mdi-hand-extended';
    return 'mdi-hand-extended-outline';
  }

  get payments() {
    return this.operation.payments;
  }

  get color() {
    if (this.payments === -1) return 'grey';
    if (this.payments === 0) return 'error';
    if (this.payments === 1) return 'orange';
    if (this.payments === 2) return 'lime';
    if (this.payments === 3) return 'light-green';
    if (this.payments === 4) return 'success';
    return '';
  }

  get title() {
    if (this.payments === -1) return '€€';
    if (this.payments === 0) return 'Ø';
    if (this.payments === 1) return '€';
    if (this.payments === 2) return '€€';
    if (this.payments === 3) return '€€½';
    if (this.payments === 4) return '€€€';
    return '';
  }

  get description() {
    if (this.payments === -1) return 'Nessun pagamento dovuto';
    if (this.payments === 0) return 'Non è stato effettuato nessun pagamento';
    if (this.payments === 1) return 'È stato effettuato il primo pagamento';
    if (this.payments === 2) return 'È stato effettuato il secondo pagamento';
    if (this.payments === 3) return 'È stato effettuato il pagamento per l\'installazione parziale';
    if (this.payments === 4) return 'È stato effettuato il pagamento finale';
    return null;
  }

  get hasPaymentsNotes(): boolean {
    return this.operation?.hasPaymentsNotes === true;
  }

  get hasPaymentsDates(): boolean {
    return this.operation?.hasPaymentsDates === true;
  }

  get columnClass(): string {
    return '';
  }

  get paymentsDetails(): string {
    const paymentsData = {
      first: {
        paymentDate: this.operation.firstPaymentDate,
        invoiceRef: this.operation.firstPaymentInvoiceRef,
        invoiceDate: this.operation.firstPaymentInvoiceDate,
        label: 'Primo pagamento',
      },
      second: {
        paymentDate: this.operation.secondPaymentDate,
        invoiceRef: this.operation.secondPaymentInvoiceRef,
        invoiceDate: this.operation.secondPaymentInvoiceDate,
        label: 'Secondo pagamento',
      },
      partialInstallation: {
        paymentDate: this.operation.partialInstallationPaymentDate,
        invoiceRef: this.operation.partialInstallationPaymentInvoiceRef,
        invoiceDate: this.operation.partialInstallationPaymentInvoiceDate,
        label: 'Pagamento install. parz.',
      },
      final: {
        paymentDate: this.operation.finalPaymentDate,
        invoiceRef: this.operation.finalPaymentInvoiceRef,
        invoiceDate: this.operation.finalPaymentInvoiceDate,
        label: 'Pagamento finale',
      },
    };

    // Le note arrivano dall'API come testo semplice
    return formatPaymentsDetails(paymentsData, this.paymentsNotes);
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  async getPaymentsNotes() {
    try {
      const paymentsNotes = await this.$store.dispatch('getPaymentsNotes', { id: this.operation.id });

      if (paymentsNotes && paymentsNotes.trim().length > 0) {
        this.paymentsNotes = paymentsNotes;
      } else {
        this.paymentsNotes = null;
      }
    } catch (error) {
      console.log('OperationPayments: error =', error); // eslint-disable-line no-console
      this.paymentsNotes = null;
    }

    return Promise.resolve();
  }

  onEdit() {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
    }, 1000);

    this.$emit('edit');
  }

  onTooltipShow() {
    // Recupera le note solo se presenti e non ancora caricate
    if (this.hasPaymentsNotes && !this.paymentsNotes) {
      this.getPaymentsNotesTimeout = setTimeout(async () => {
        this.getPaymentsNotes();
      }, 1000);
    }
  }

  onTooltipHide() {
    clearTimeout(this.getPaymentsNotesTimeout);
  }
}
